import React from "react";
import { Star, Quote } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { reviews } from "@/data/reviews";
import Reveal from "@/components/Reveal";

const Stars = () => (
  <div className="flex items-center gap-0.5" aria-label="5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" />
    ))}
  </div>
);

export const Reviews = () => {
  const { t } = useLang();

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="section-pad bg-[#F6F1E8] relative"
    >
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[#003366]/90" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <Reveal className="max-w-2xl">
            <span className="ornament text-[#E2725B]">{t.reviews.overline}</span>
            <h2
              data-testid="reviews-title"
              className="mt-6 font-serif text-[#003366] text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight whitespace-pre-line"
            >
              {t.reviews.title}
            </h2>
            <p className="mt-6 text-[#2D3748] text-base md:text-lg leading-relaxed">
              {t.reviews.subtitle}
            </p>
          </Reveal>
          <Reveal
            delay={150}
            data-testid="reviews-badge"
            className="inline-flex items-center gap-3 bg-white border border-[#708090]/25 px-5 py-3 self-start md:self-end"
          >
            <Stars />
            <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#003366]">
              {t.reviews.badge}
            </span>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {reviews.map((r, idx) => (
            <Reveal
              key={r.id}
              delay={idx * 90}
              as="article"
              data-testid={`review-card-${r.id}`}
              className={`relative bg-white border border-[#708090]/20 p-7 md:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 shadow-[0_14px_40px_-18px_rgba(120,80,40,0.35)] hover:shadow-[0_24px_60px_-20px_rgba(120,80,40,0.45)] ${
                idx === 0 ? "md:col-span-2 lg:col-span-2" : ""
              }`}
            >
              <Quote className="h-6 w-6 text-[#E2725B] mb-4 opacity-80" />
              <p className="font-serif text-[#003366] text-lg md:text-xl leading-snug flex-1">
                “{r.quote}”
              </p>
              <div className="mt-6 flex items-center justify-between gap-4 pt-5 border-t border-[#708090]/15">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#003366] text-white font-semibold flex items-center justify-center text-sm">
                    {r.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#2D3748]">
                      {r.author}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#708090]">
                      {r.origin}
                    </div>
                  </div>
                </div>
                <Stars />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
